import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { isUploadError } from '@/lib/upload'
import type { ClinicLogo, ClinicProfileInput, LogoMimeType, LogoPosition } from './api'

/**
 * O logo da clínica na tela de Empresa (RN-ADM-05): PNG ou JPEG de verdade, até 2 MB, pelo menos
 * 200×200px, proporção entre 1:2 e 2:1. A tela confere o arquivo antes de enviar, lendo os bytes
 * como a API lê ao confirmar, e com as mesmas mensagens: o erro diz o mesmo dos dois lados.
 */

export const LOGO_MAX_BYTES = 2 * 1024 * 1024
export const LOGO_MIN_SIDE = 200
/** O que o seletor de arquivo oferece; quem decide o formato são os bytes, não a extensão. */
export const LOGO_ACCEPT = 'image/png,image/jpeg'

export const MSG_LOGO_FORMAT = 'Envie PNG ou JPEG.'
export const MSG_LOGO_SIZE = 'A imagem passa de 2 MB.'
export const MSG_LOGO_RATIO = 'A imagem precisa ter proporção entre 1:2 e 2:1.'

export const LOGO_POSITION_LABELS: Record<LogoPosition, string> = {
  header: 'Cabeçalho',
  watermark: "Marca d'água",
}

// O formulário ----------------------------------------------------------------------------------

/**
 * O que a pessoa fez com o logo nesta edição. Nada vale antes de salvar: `replace` guarda o
 * `logoId` do envio e a prévia local; `remove` tira o logo; `keep` deixa como está.
 */
export type LogoChange =
  | { action: 'keep' }
  | { action: 'remove' }
  | { action: 'replace'; logoId: string; previewUrl: string; width: number; height: number }

export const KEEP_LOGO: LogoChange = { action: 'keep' }

/** A parte do formulário de Empresa que é do logo. */
export interface LogoFormState {
  logoPosition: LogoPosition
  logo: LogoChange
}

export function logoFormFrom(clinic: { logoPosition: LogoPosition }): LogoFormState {
  return { logoPosition: clinic.logoPosition, logo: KEEP_LOGO }
}

/** A posição vai sempre; `logoId` só quando a pessoa trocou ou removeu o logo. */
export function logoInput(values: LogoFormState) {
  const { logo, logoPosition } = values
  if (logo.action === 'keep') return { logoPosition } satisfies ClinicProfileInput
  return {
    logoPosition,
    logoId: logo.action === 'replace' ? logo.logoId : null,
  } satisfies ClinicProfileInput
}

/** Os caminhos do 422 que são do logo, na chave do formulário. */
export const LOGO_FIELD_KEYS: Record<string, keyof LogoFormState> = {
  logo: 'logo',
  logoId: 'logo',
  logoPosition: 'logoPosition',
}

/** O que a pré-visualização mostra: o que foi enviado agora, nada (removido) ou o logo salvo. */
export function logoPreview(
  change: LogoChange,
  saved: ClinicLogo | null,
): { url: string; width: number; height: number } | null {
  if (change.action === 'replace') {
    return { url: change.previewUrl, width: change.width, height: change.height }
  }
  if (change.action === 'remove' || !saved) return null
  return { url: saved.url, width: saved.width, height: saved.height }
}

// A imagem, pelos bytes -------------------------------------------------------------------------

export interface LogoImage {
  mimeType: LogoMimeType
  width: number
  height: number
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

/** PNG: assinatura (8 bytes), depois o bloco IHDR, que abre com a largura e a altura. */
function readPng(bytes: Uint8Array, view: DataView): LogoImage | null {
  if (bytes.length < 24 || !PNG_SIGNATURE.every((byte, i) => bytes[i] === byte)) return null
  if (String.fromCharCode(...bytes.subarray(12, 16)) !== 'IHDR') return null
  return { mimeType: 'image/png', width: view.getUint32(16), height: view.getUint32(20) }
}

/** Marcadores de quadro (SOF0 a SOF15), fora os três que usam a mesma faixa para outra coisa. */
function isFrameMarker(marker: number) {
  return marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc
}

/** JPEG: percorre os segmentos até o quadro (SOF), que traz a altura e a largura. */
function readJpeg(bytes: Uint8Array, view: DataView): LogoImage | null {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null
  let at = 2
  while (at + 3 < bytes.length) {
    if (bytes[at] !== 0xff) return null
    const marker = bytes[at + 1]!
    // Preenchimento entre segmentos.
    if (marker === 0xff) {
      at += 1
      continue
    }
    // Marcadores sem tamanho (TEM e os de reinício).
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      at += 2
      continue
    }
    // Os dados da imagem (ou o fim) chegaram antes de qualquer quadro: não dá para medir.
    if (marker === 0xda || marker === 0xd9) return null
    const length = view.getUint16(at + 2)
    if (length < 2) return null
    if (isFrameMarker(marker)) {
      if (at + 9 > bytes.length) return null
      return {
        mimeType: 'image/jpeg',
        width: view.getUint16(at + 7),
        height: view.getUint16(at + 5),
      }
    }
    at += 2 + length
  }
  return null
}

/** O formato e as dimensões lidos dos bytes; `null` quando não é PNG nem JPEG legível. */
export function readLogoImage(bytes: Uint8Array): LogoImage | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  return readPng(bytes, view) ?? readJpeg(bytes, view)
}

export type LogoInspection = { ok: true; image: LogoImage } | { ok: false; message: string }

/**
 * Confere o arquivo escolhido antes de enviar, na ordem da API: formato, tamanho, dimensões e
 * proporção. Arquivo acima do limite não é lido inteiro: bastam os primeiros bytes para dizer se é
 * imagem.
 */
export async function inspectLogoFile(file: Blob): Promise<LogoInspection> {
  const isOversized = file.size > LOGO_MAX_BYTES
  const bytes = new Uint8Array(
    await (isOversized ? file.slice(0, LOGO_MAX_BYTES) : file).arrayBuffer(),
  )
  const image = readLogoImage(bytes)
  if (!image) return { ok: false, message: MSG_LOGO_FORMAT }
  if (isOversized) return { ok: false, message: MSG_LOGO_SIZE }
  if (image.width < LOGO_MIN_SIDE || image.height < LOGO_MIN_SIDE) {
    return {
      ok: false,
      message: `A imagem tem ${image.width}×${image.height}px; o mínimo é ${LOGO_MIN_SIDE}×${LOGO_MIN_SIDE}px.`,
    }
  }
  if (image.width > image.height * 2 || image.height > image.width * 2) {
    return { ok: false, message: MSG_LOGO_RATIO }
  }
  return { ok: true, image }
}

// Falha do envio --------------------------------------------------------------------------------

/** A frase de uma falha ao reservar ou enviar o logo, com causa e caminho. */
export function logoUploadProblem(error: unknown): string {
  if (isUploadError(error)) {
    if (error.code === 'too_large') return MSG_LOGO_SIZE
    if (error.code === 'unsupported_type') return MSG_LOGO_FORMAT
    if (error.code === 'network_error') {
      return 'Não conseguimos enviar a imagem. Confira a conexão e escolha o arquivo de novo.'
    }
    return 'O envio da imagem foi recusado. Escolha o arquivo de novo em instantes.'
  }
  // A reserva recusada pela API (422) diz o campo: formato ou tamanho.
  if (isApiError(error)) {
    const [first] = Object.values(fieldErrors(error))
    if (first) return first
  }
  return errorMessage(error)
}

/** O nome do arquivo como a API aceita: sem espaços nas pontas e com até 120 caracteres. */
export function logoFileName(name: string) {
  return name.trim().slice(0, 120) || 'logo'
}
